<template>
  <div>
    <Loader
      v-if="loading && (!nodes || !nodes.length)"
      color="black"
      size="small"
      class="my-1"
    ></Loader>

    <div
      v-if="popup && !loading && (!nodes || !nodes.length)"
      class="pt-3 px-3 pb-1 hyp-popup-text"
    >
      <span v-if="!hasWatchOnly">
        Save content to folders.<br />
        Click the green Add Folder<br />
        button to add a new folder. <br />
        Click the new folder to save<br />
        the content to that folder.
      </span>
      <span v-if="hasWatchOnly">
        As soon as users in your group share<br />
        folders with you the list of folders<br />
        will appear here so that you can<br />
        select the folder you want to<br />
        save content to.
      </span>
    </div>

    <div
      v-if="!popup && !loading && (!nodes || !nodes.length)"
      class="mt-3 mr-2"
    >
      <p>
        <span v-if="!hasWatchOnly"
          >You can create folders and subfolders to save and organize your
          content.</span
        >
        <span v-if="hasWatchOnly"
          >Users in your group will share folders with you to allow you to view
          and save content within the folders.</span
        >
        When searching for content you will be able to click a small folder
        <v-icon size="15" color="black" class="px-1">fas fa-folder-open</v-icon>
        icon near the content title to save that content into a folder.
      </p>
      <p v-if="!hasWatchOnly">
        Click on the green Add Folder button below to start adding folders. Once
        a folder is created you can rename the folder or create a subfolder by
        clicking the three dotted
        <v-icon size="15" color="black" class="px-1">fas fa-ellipsis-v</v-icon>
        icon to the right of the folder name.
      </p>
    </div>

    <v-row dense row :class="{ 'hyp-tool-container': !popup }">
      <v-col class="text-left col-3">
        <v-btn
          v-if="!loading && !hasWatchOnly && !isInMaintenanceMode"
          :disabled="saveBtnDisabled"
          small
          fab
          color="success"
          title="Add new folder"
          class="ml-1"
          :class="{
            'hyp-popup-add-folder': !popup,
            'hyp-popup-add-folder-popup': popup,
          }"
          @click="handleClickAdd({})"
        >
          <v-icon
            v-if="!saveBtnDisabled"
            dark
            :class="{
              'hyp-popup-add-folder-icon': !popup,
              'hyp-popup-add-folder-icon-popup': popup,
            }"
            >fas fa-folder-plus</v-icon
          >
          <v-progress-circular
            v-if="saveBtnDisabled"
            indeterminate
            color="#0000008a"
            :size="popup ? 12 : 16"
          ></v-progress-circular>
        </v-btn>
      </v-col>
      <v-col class="col-9">
        <v-text-field
          v-if="!loading && nodes && nodes.length"
          v-model="search"
          :disabled="loading"
          placeholder="Filter folders"
          class="pull-right mr-3 mt-0 mb-3 pt-0 hyp-tree-search"
          :class="{
            'hyp-tree-search': !popup,
            'hyp-tree-search-popup': popup,
          }"
        >
          <template v-slot:append>
            <v-icon
              v-if="!loading"
              :size="13"
              class="mt-1 hyp-filter-icon"
              @click="handleFilterClick"
              >{{ searchIcon }}</v-icon
            >
          </template>
        </v-text-field>
        <div class="clearfix"></div>
      </v-col>
    </v-row>

    <div v-if="!loading" class="mt-3 mr-2">
      <v-treeview
        v-if="nodes && nodes.length"
        ref="thetree"
        v-model="tree"
        :items="nodes"
        :active.sync="active"
        :search="search"
        :filter="filter"
        return-object
        item-key="id"
        hoverable
        open-all
        dense
        :close-on-click="popup"
        class="pl-3 mt-1 mb-1"
        :class="{
          'hyp-tree': !popup,
          'hyp-tree-popup': popup,
        }"
      >
        <template #prepend="{ item }">
          <div v-if="!item.id" class="hiddenchild"></div>
          <v-icon v-if="item.saving" size="11" class="mt-2 hyp-tree-node-saving"
            >fas fa-spinner fa-spin</v-icon
          >
          <v-icon
            v-if="item.saved"
            size="11"
            color="green"
            class="mt-2 hyp-tree-node-saving"
            >fas fa-check</v-icon
          >
          <v-icon
            v-if="item.saveFailed"
            size="11"
            color="red"
            class="mt-2 hyp-tree-node-saving"
          >
            fas fa-times
          </v-icon>
          <v-icon
            size="18"
            class="mx-1"
            :class="{ 'hyp-tree-node-no-children': !item.id }"
            @mousemove="handleHover(item, true)"
            @mouseout="handleHover(item, false)"
            @click="handleSelect(item)"
            >far fa-folder-open</v-icon
          >
        </template>
        <template v-slot:label="{ item }">
          <div
            class="hyp-tree-label"
            :class="{ 'hyp-tree-node-no-children': !item.id }"
            @mousemove="handleHover(item, true)"
            @mouseout="handleHover(item, false)"
            @click="handleSelect(item)"
          >
            <v-menu
              v-if="
                !isInMaintenanceMode && !(item.parent && isLinkedFolder(item))
              "
              open-on-click
              offset-x
              right
              rounded="lg"
            >
              <template v-slot:activator="{ on: menu }">
                <v-icon
                  v-on="menu"
                  class="pull-right mr-2 mt-1 hyp-options-icon"
                  size="14"
                  >fas fa-ellipsis-v</v-icon
                >
              </template>

              <v-list>
                <v-list-item
                  :disabled="!item.isOwner || isLinkedFolder(item)"
                  @click="handleClickEdit(item)"
                >
                  <v-list-item-title>
                    <v-icon
                      size="16"
                      class="mr-1"
                      :disabled="!item.isOwner || isLinkedFolder(item)"
                      >fas fa-pencil-alt</v-icon
                    >
                    Rename folder
                  </v-list-item-title>
                </v-list-item>
                <v-list-item
                  :disabled="!item.isOwner"
                  v-if="!item.parent && !hasWatchOnly"
                  @click="handleClickAdd(item)"
                >
                  <v-list-item-title>
                    <v-progress-circular
                      v-if="loadingAdd"
                      indeterminate
                      color="#0000008a"
                      size="16"
                      class="mr-1"
                    ></v-progress-circular>
                    <v-icon v-if="!loadingAdd" size="16" class="mr-1"
                      >fas fa-plus</v-icon
                    >
                    Add subfolder
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <div
              class="hyp-tree-label-text"
              :class="{ 'hyp-text-popup': popup }"
              @mousemove="handleHover(item, true)"
              @mouseout="handleHover(item, false)"
            >
              <span v-if="!item.edit">{{ item.name }}</span>

              <v-icon
                v-if="!item.isOwner"
                size="10"
                class="ml-2"
                :title="`Shared folder`"
                >fas fa-share</v-icon
              >

              <v-icon
                v-if="!!(item.linkedWith && item.linkedWith.length)"
                size="10"
                class="ml-2"
                :title="`Linked folder`"
                >fas fa-link</v-icon
              >

              <input
                :id="`input-${item.id}`"
                type="text"
                v-if="item.edit"
                v-model="item.name"
                class="hyp-tree-label-input"
                @keyup="handleKeyUpEditSave"
              />
            </div>
            <div class="clearfix"></div>
          </div>
        </template>
      </v-treeview>
    </div>

    <ConfirmationPopup
      :show="confirmationShow"
      :title="confirmationTitle"
      :has-text-input="true"
      :text-input-placeholder="confirmationInputPlaceholder"
      :buttonCancel="confirmationBtnCancel"
      :buttonAction="confirmationBtnAction"
    ></ConfirmationPopup>
  </div>
</template>

<script>
import lodashCloneDeep from "lodash/cloneDeep";
import lodashRemove from "lodash/remove";
import lodashDebounce from "lodash/debounce";

import { mapGetters } from "vuex";

import {
  GET_NOTIFIER,
  GET_COLLABORATION_FOLDERS,
  GET_COLLABORATION_FOLDERS_CHANGED,
} from "@/store/actions.type";
import { MODULE_PREFIX_HOME, MODULE_PREFIX_COLLABORATION } from "@/store";
import { NOTIFIER_TYPES } from "@/common/constants/misc";
import {
  saveFolder,
  saveArticleToFolder,
} from "@/services/collaboration/folders.service";
import { hasWatchOnlyAccess } from "@/services/user/user.service";

import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  HAS_FOLDERS: "has-folders",
  CLICK_SELECT: "click-select",
  CLICK_ADD: "click-add",
  CLICK_EDIT: "click-edit",
  CLICK_EDIT_SAVE: "click-edit-save",
  CLICK_EDIT_CANCEL: "click-edit-cancel",
};

export default {
  name: "CollaborationTree",
  components: {
    Loader: () => import("@/components/common/Loader"),
    ConfirmationPopup: () => import("@/components/common/ConfirmationPopup"),
  },
  props: {
    initialize: {
      type: String,
      default: null,
    },
    popup: {
      type: Boolean,
      default: false,
    },
    article: {
      type: Object,
    },
    showingDocuments: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    hasWatchOnly: hasWatchOnlyAccess(),
    tree: [],
    nodes: [],
    active: [],
    hidden: [],
    loading: true,
    over: false,
    currentEditFolder: null,
    tempName: null,
    saveBtnDisabled: false,
    loadingAdd: false,
    search: null,
    selectedFolder: null,
    confirmationShow: false,
    addedId: null,
  }),
  created() {
    window.addEventListener("click", this.editNodeClick, true);
  },
  computed: {
    ...mapGetters({
      notifier: MODULE_PREFIX_HOME + "notifier",
      folders: MODULE_PREFIX_COLLABORATION + "folders",
      foldersChange: MODULE_PREFIX_COLLABORATION + "foldersChange",
    }),
    isInMaintenanceMode() {
      // if collab is in maintenance mode then we need to not allow user to create new folders
      return this.featureUnlocked("maintenance-collaboration");
    },
    confirmationTitle() {
      return this.selectedFolder && this.selectedFolder.id
        ? `Add new sub folder`
        : "Add new folder";
    },
    confirmationInputPlaceholder() {
      return this.selectedFolder && this.selectedFolder.id
        ? `Enter sub folder name`
        : "Enter folder name";
    },
    confirmationBtnCancel() {
      return {
        text: "Cancel",
        onClickFnc: () => {
          this.setConfirmationShow(false);
        },
      };
    },
    confirmationBtnAction() {
      return {
        text: "Add new",
        disabled: true,
        onClickFnc: (params) => {
          this.setConfirmationShow(false);
          this.addNewFolder(params.textfield);
        },
      };
    },
    filter() {
      return (item, search, textKey) => {
        if (item && textKey && item[textKey] && search) {
          return item[textKey].toLowerCase().includes(search.toLowerCase());
        }
      };
    },
    searchIcon() {
      return this.search ? "fas fa-times" : "fas fa-filter";
    },
  },
  mounted() {
    if (this.folders) {
      this.getUserFolders();
    }
  },
  methods: {
    getUserFolders() {
      this.loading = true;
      let folders = lodashCloneDeep(this.folders);
      if (folders) {
        lodashRemove(folders, (node) => {
          return !node.hasOwnProperty("id");
        });
        this.nodes = folders;
      }
      this.hideRootChildClasses();
      this.$emit(
        EMITTED_ACTIONS.HAS_FOLDERS,
        !!(this.nodes && this.nodes.length)
      );
      this.loading = false;

      if (this.$refs && this.$refs.thetree) {
        this.$refs.thetree.updateAll(false);
        this.$refs.thetree.updateAll(true);
      }

      this.selectFolderAfterRefresh();
    },
    selectFolderAfterRefresh() {
      if (this.addedId) {
        this.handleSelect(this.getNodeById(this.addedId), true, true);
        this.addedId = null;
        return;
      }

      let activeFolderPresent = false;
      if (this.active && this.active[0]) {
        let checkId = this.active[0].id;
        for (let i = 0; i < this.folders.length; i++) {
          if (this.folders[i].id === checkId) {
            activeFolderPresent = true;
          }
        }
      }

      if (activeFolderPresent) {
        this.handleSelect(this.active[0], true);
      } else {
        this.handleSelect(this.nodes[0], true);
      }
    },
    scrollToFolder() {
      lodashDebounce(() => {
        let folder = this.$el.querySelector(".v-treeview-node--active");
        let container = document.querySelector(".hyp-collab-tree-container");
        if (folder && container) {
          this.$vuetify.goTo(folder, {
            duration: 750,
            container: container,
          });
        }
      }, 1000)();
    },
    hideRootChildClasses() {
      // This function is sort of a hack due to the fact that if one of the
      // parent nodes in the Tree don't have children they get offset within
      // the tree. So we're adding a single child to a root new parent with
      // that child having a null id. Then using this function to hide these
      // children so that the root node isn't offset.
      lodashDebounce(() => {
        const ELEMS = document.querySelectorAll(".hiddenchild");
        ELEMS.forEach((elem) => {
          if (
            elem &&
            elem.parentElement &&
            elem.parentElement.className === "v-treeview-node__prepend" &&
            elem.parentElement.parentElement &&
            elem.parentElement.parentElement.className ===
              "v-treeview-node__content" &&
            elem.parentElement.parentElement.parentElement &&
            elem.parentElement.parentElement.parentElement.className ===
              "v-treeview-node__root"
          ) {
            elem.parentElement.parentElement.parentElement.style.minHeight =
              "4px";
            elem.parentElement.parentElement.parentElement.style.display =
              "none";
          }
        });
      }, 10)();
    },
    handleFilterClick() {
      this.hideRootChildClasses();
      this.search = null;
    },
    getNodeById(id) {
      for (let i = 0; i < this.nodes.length; i++) {
        if (this.nodes[i].id === id) {
          return this.nodes[i];
        }
        if (this.nodes[i].children) {
          for (let j = 0; j < this.nodes[i].children.length; j++) {
            if (this.nodes[i].children[j].id === id) {
              return this.nodes[i].children[j];
            }
          }
        }
      }
    },
    async handleSelect(item, dontSaveArticle, scrollToActive) {
      if (item && item.id) {
        let node = this.getNodeById(item.id);
        this.$set(this.active, 0, node);

        if (scrollToActive) {
          this.scrollToFolder();
        }

        if (this.article && !dontSaveArticle) {
          item.saving = true;
          let article = lodashCloneDeep(this.article);

          // saving an article when already within another folder
          // so we need to delete the article.id so it doesn't
          // just try to update the article
          if (this.showingDocuments && article.id) {
            delete article.id;
          }

          let saveError = false;
          try {
            await saveArticleToFolder({
              article: article,
              folder: item,
            });
          } catch (err) {
            saveError = "Failed : " + err.message;
          }
          if (saveError) {
            this.notifier.visible = true;
            this.notifier.type = NOTIFIER_TYPES.error;
            this.notifier.message = saveError;
            this.$store.dispatch(
              MODULE_PREFIX_HOME + GET_NOTIFIER,
              this.notifier
            );
            item.saveFailed = true;
          } else {
            item.saved = true;
          }
          item.saving = false;

          await this.$store.dispatch(
            MODULE_PREFIX_COLLABORATION + GET_COLLABORATION_FOLDERS_CHANGED,
            true
          );
          this.$store.dispatch(
            MODULE_PREFIX_COLLABORATION + GET_COLLABORATION_FOLDERS
          );
        }
        this.$emit(EMITTED_ACTIONS.CLICK_SELECT, lodashCloneDeep(item));
      }
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.collaboration,
        EVENT_ACTIONS.clicked,
        "Content Folder - Folder"
      );
    },
    handleClickEdit(item) {
      if (item) {
        this.clearEdit();
        item.edit = true;
        this.currentEditFolder = item;
        this.tempName = lodashCloneDeep(item.name);
        lodashDebounce(() => {
          const ELEM = document.querySelector(
            `#input-${this.currentEditFolder.id}`
          );
          if (ELEM) {
            ELEM.select();
          }
        }, 1000)();
        this.$emit(EMITTED_ACTIONS.CLICK_EDIT, lodashCloneDeep(item));
      } else {
        this.notifier.visible = true;
        this.notifier.type = NOTIFIER_TYPES.error;
        this.notifier.message = "Unable to edit folder.<br>Please try again";
        this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);
      }
    },
    async editSave() {
      if (this.currentEditFolder && this.currentEditFolder.id) {
        await saveFolder({ folder: this.currentEditFolder });
        this.$emit(
          EMITTED_ACTIONS.CLICK_EDIT_SAVE,
          lodashCloneDeep(this.currentEditFolder)
        );
        await this.$store.dispatch(
          MODULE_PREFIX_COLLABORATION + GET_COLLABORATION_FOLDERS_CHANGED,
          true
        );
        await this.$store.dispatch(
          MODULE_PREFIX_COLLABORATION + GET_COLLABORATION_FOLDERS
        );
      }
    },
    handleKeyUpEditSave(evt) {
      if (evt && evt.key && evt.key.toLowerCase() === "enter") {
        this.currentEditFolder.edit = false;
        this.currentEditFolder.hover = false;
        this.currentEditFolder.saving = false;
        this.editSave();
      }
    },
    editNodeClick(evt) {
      if (this.currentEditFolder) {
        const CURRENT_NODE_ELM = document.querySelector(
          `#input-${this.currentEditFolder.id}`
        );
        const EDIT_ICON_ELM = document.querySelector(
          `#edit-icon-${this.currentEditFolder.id}`
        );
        let targetElement = evt.target;

        do {
          if (
            targetElement == CURRENT_NODE_ELM ||
            targetElement == EDIT_ICON_ELM
          ) {
            return; // inside of textbox
          }
          targetElement = targetElement.parentNode; // go up the DOM
        } while (targetElement);

        // outside of textbox
        if (this.currentEditFolder && this.currentEditFolder.edit) {
          this.currentEditFolder.edit = false;
          this.currentEditFolder.hover = false;
          this.currentEditFolder.saving = false;
          this.editSave();
        }
      }
    },
    handleClickAdd(item) {
      this.selectedFolder = item;
      this.setConfirmationShow(true);
    },
    async addNewFolder(name) {
      const item = this.selectedFolder;
      this.loadingAdd = true;
      let folder = {
        name: name,
        parent: item.id ? item.id : null,
      };
      this.saveBtnDisabled = true;
      const RESULT = await saveFolder({ folder: folder });
      if (RESULT) {
        this.clearEdit(true);
        this.saveBtnDisabled = false;
        this.loadingAdd = false;
        this.$emit(EMITTED_ACTIONS.CLICK_ADD, RESULT);
        if (this.popup) {
          await this.$store.dispatch(
            MODULE_PREFIX_COLLABORATION + GET_COLLABORATION_FOLDERS,
            { force: true }
          );
        }
        this.addedId = RESULT;
      } else {
        this.notifier.visible = true;
        this.notifier.type = NOTIFIER_TYPES.error;
        this.notifier.message = "Unable to add new folder.<br>Please try again";
        this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);
        this.saveBtnDisabled = false;
        this.loadingAdd = false;
      }
      this.selectedFolder = null;
    },
    clearEdit(ignoreName) {
      if (this.currentEditFolder) {
        this.currentEditFolder.edit = false;
        if (!ignoreName) {
          this.currentEditFolder.name = lodashCloneDeep(this.tempName);
        }
        this.currentEditFolder = null;
      }
    },
    handleHover(item, state) {
      item.hover = state;
    },
    isLinkedFolder(item) {
      return (
        item &&
        item.isOwner &&
        item.parent &&
        item.hasOwnProperty("originalFolderId") &&
        item.originalFolderId !== item.id
      );
    },
    setConfirmationShow(value) {
      this.confirmationShow = value;
    },
  },
  watch: {
    folders: {
      async handler() {
        await this.getUserFolders();
      },
    },
    initialize: {
      async handler(newValue, oldValue) {
        if (newValue != oldValue) {
          this.search = null;
          await this.$store.dispatch(
            MODULE_PREFIX_COLLABORATION + GET_COLLABORATION_FOLDERS,
            { force: true }
          );
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.tree = null;
    this.nodes = null;
    this.active = null;
    this.hidden = null;
    this.currentEditFolder = null;
    this.tempName = null;
    this.search = null;
  },
};
</script>

<style>
.v-treeview.hyp-tree .v-treeview-node,
.v-treeview.hyp-tree .v-treeview-node__root {
  margin-bottom: 15px !important;
}
.v-treeview.hyp-tree .v-treeview-node--leaf,
.v-treeview.hyp-tree-popup .v-treeview-node--leaf {
  margin-left: 30px !important;
}
.v-treeview-node__toggle {
  display: none !important;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1.5s ease-out !important;
}
.fade-enter,
.fade-leave-to {
  opacity: 0 !important;
}
.hyp-tree-search .v-input__control {
  height: 12px !important;
}
</style>

<style scoped>
.hyp-tool-container {
  position: relative;
  top: -6px;
}
.hyp-tree-popup {
  min-width: 200px !important;
  max-height: 300px !important;
}
.hyp-tree-node-no-children {
  display: none;
}
.hyp-popup-add-folder {
  height: 32px !important;
  width: 32px !important;
}
.hyp-popup-add-folder-popup {
  height: 24px !important;
  width: 24px !important;
}
.hyp-popup-add-folder-icon {
  font-size: 16px !important;
}
.hyp-popup-add-folder-icon-popup {
  font-size: 12px !important;
}
.hyp-tree-label {
  cursor: pointer !important;
}
.hyp-tree-label-input {
  width: 125px;
  border: 3px solid #1976d2;
  border-radius: 8px;
}
.hyp-tree-label-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}
.hyp-text-popup {
  font-size: 12px;
}
.hyp-tree-icon {
  position: relative;
  top: -2px;
  cursor: pointer;
}
.hyp-tree-icon-text {
  font-size: 10px;
}
.hyp-tree-node-saving {
  position: absolute;
  left: 2px;
}
.hyp-options-icon {
  margin-top: 5px !important;
  color: rgba(0, 0, 0, 0.24) !important;
}
.hyp-options-menu {
  border-radius: 8px !important;
}
.hyp-tree-search {
  width: 140px;
  font-size: 14px;
}
.hyp-tree-search-popup {
  width: 105px;
  font-size: 12px;
}
.hyp-popup-text {
  font-size: 12px;
}
.hyp-filter-icon {
  position: relative;
  top: 3px;
}
</style>
